/**
 * Hook pour générer et uploader des visuels brandés vers Supabase Storage
 */
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import logoImage from '@/assets/regard-de-nature-logo.png';

export interface RenderedMedia {
  feed_url: string | null;
  story_url: string | null;
}

export interface RenderOptions {
  coverImageUrl: string | null;
  visualTitle: string | null;
  showLogo: boolean;
  showSiteName: boolean;
  draftId: string;
}

/**
 * Helper pour charger une image de façon async avec gestion CORS
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => resolve(img);
    img.onerror = (e) => {
      console.warn(`Failed to load image: ${src}`, e);
      reject(new Error(`Failed to load image: ${src}`));
    };
    
    // Add cache-busting for external URLs to avoid CORS caching issues
    if (src.startsWith('http')) {
      const separator = src.includes('?') ? '&' : '?';
      img.src = `${src}${separator}t=${Date.now()}`;
    } else {
      img.src = src;
    }
  });
}

export function useRenderedMediaUpload() {
  const [isRendering, setIsRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Upload un blob d'image vers Supabase Storage
   */
  const uploadToStorage = async (
    blob: Blob, 
    draftId: string, 
    format: 'feed' | 'story'
  ): Promise<string | null> => {
    console.log(`[uploadToStorage] START - format: ${format}, draftId: ${draftId}, blob size: ${blob.size} bytes`);
    
    try {
      const timestamp = Date.now();
      const fileName = `social-render/${draftId}/${format}-${timestamp}.jpg`;
      console.log(`[uploadToStorage] Uploading to: assets/${fileName}`);
      
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('assets')
        .upload(fileName, blob, {
          contentType: 'image/jpeg',
          upsert: true,
        });

      if (uploadError) {
        console.error('[uploadToStorage] Upload error:', uploadError);
        throw uploadError;
      }
      
      console.log(`[uploadToStorage] Upload successful:`, uploadData);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('assets')
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl || null;
      console.log(`[uploadToStorage] Public URL: ${publicUrl}`);
      
      return publicUrl;
    } catch (err) {
      console.error('[uploadToStorage] FAILED:', err);
      throw err;
    }
  };

  /**
   * Génère une image brandée via Canvas API
   */
  const renderBrandedImage = useCallback(async (
    format: 'feed' | 'story',
    options: RenderOptions
  ): Promise<string | null> => {
    const { coverImageUrl, visualTitle, showLogo, showSiteName, draftId } = options;
    
    const width = 1080;
    const height = format === 'feed' ? 1080 : 1920;
    
    console.log(`[renderBrandedImage] START - format: ${format}, size: ${width}x${height}`);
    console.log(`[renderBrandedImage] Options:`, { coverImageUrl: coverImageUrl?.substring(0, 50), visualTitle, showLogo, showSiteName, draftId });

    return new Promise(async (resolve) => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.error('Failed to get canvas context');
          resolve(null);
          return;
        }

        // Background color (forest green)
        ctx.fillStyle = '#506842';
        ctx.fillRect(0, 0, width, height);

        // Load and draw cover image if available
        if (coverImageUrl) {
          try {
            const bgImage = await loadImage(coverImageUrl);
            // Cover fit
            const imgRatio = bgImage.width / bgImage.height;
            const canvasRatio = width / height;
            let drawWidth: number;
            let drawHeight: number;
            let offsetX: number;
            let offsetY: number;
            
            if (imgRatio > canvasRatio) {
              drawHeight = height;
              drawWidth = bgImage.width * (height / bgImage.height);
              offsetX = -(drawWidth - width) / 2;
              offsetY = 0;
            } else {
              drawWidth = width;
              drawHeight = bgImage.height * (width / bgImage.width);
              offsetX = 0;
              offsetY = -(drawHeight - height) / 2;
            }
            
            ctx.drawImage(bgImage, offsetX, offsetY, drawWidth, drawHeight);
          } catch (e) {
            console.warn('Failed to load cover image:', e);
          }
        }

        // Overlay gradient for readability
        const gradient = ctx.createLinearGradient(0, height, 0, 0);
        gradient.addColorStop(0, 'rgba(0,0,0,0.7)');
        gradient.addColorStop(0.4, 'transparent');
        gradient.addColorStop(1, 'rgba(0,0,0,0.4)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        // Header branding
        if (showLogo || showSiteName) {
          let xOffset = 40;
          
          if (showLogo) {
            try {
              // Use imported logo (bundled asset) - more reliable than URL
              const logoImg = await loadImage(logoImage);
              const logoSize = 100;
              
              // White circle background
              ctx.save();
              ctx.beginPath();
              ctx.arc(xOffset + logoSize / 2, 40 + logoSize / 2, logoSize / 2 + 4, 0, Math.PI * 2);
              ctx.fillStyle = 'rgba(255,255,255,0.9)';
              ctx.fill();
              
              ctx.beginPath();
              ctx.arc(xOffset + logoSize / 2, 40 + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
              ctx.clip();
              ctx.drawImage(logoImg, xOffset, 40, logoSize, logoSize);
              ctx.restore();
              
              xOffset += logoSize + 24;
            } catch (e) {
              console.warn('Failed to load logo, drawing placeholder:', e);
              // Draw placeholder circle
              ctx.save();
              ctx.beginPath();
              ctx.arc(40 + 50, 40 + 50, 54, 0, Math.PI * 2);
              ctx.fillStyle = 'rgba(255,255,255,0.9)';
              ctx.fill();
              ctx.beginPath();
              ctx.arc(40 + 50, 40 + 50, 50, 0, Math.PI * 2);
              ctx.fillStyle = '#506842';
              ctx.fill();
              ctx.restore();
              xOffset += 100 + 24;
            }
          }

          if (showSiteName) {
            ctx.shadowColor = 'rgba(0,0,0,0.9)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetY = 2;
            
            ctx.font = 'bold 36px Cinzel, serif';
            ctx.fillStyle = '#CCA727'; // Gold
            ctx.textAlign = 'left';
            ctx.fillText('REGARD', xOffset, 70);
            
            ctx.fillStyle = 'white';
            ctx.fillText('DE NATURE', xOffset, 110);
            
            ctx.shadowBlur = 0;
          }
        }

        // Visual title (centered)
        if (visualTitle) {
          ctx.shadowColor = 'rgba(0,0,0,1)';
          ctx.shadowBlur = 20;
          ctx.shadowOffsetY = 4;
          
          const fontSize = format === 'feed' ? 72 : 64;
          ctx.font = `bold ${fontSize}px Cinzel, serif`;
          ctx.fillStyle = 'white';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          // Word wrap
          const maxWidth = width * 0.8;
          const words = visualTitle.split(' ');
          const lines: string[] = [];
          let currentLine = '';
          
          for (const word of words) {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth && currentLine) {
              lines.push(currentLine);
              currentLine = word;
            } else {
              currentLine = testLine;
            }
          }
          if (currentLine) lines.push(currentLine);
          
          const lineHeight = fontSize * 1.2;
          const totalHeight = lines.length * lineHeight;
          const startY = (height - totalHeight) / 2;
          
          lines.forEach((line, i) => {
            ctx.fillText(line, width / 2, startY + i * lineHeight + lineHeight / 2);
          });
          
          ctx.shadowBlur = 0;
        }

        // Footer URL
        ctx.shadowColor = 'rgba(0,0,0,0.9)';
        ctx.shadowBlur = 8;
        ctx.shadowOffsetY = 2;
        
        ctx.font = '500 28px Montserrat, sans-serif';
        ctx.fillStyle = '#CCA727';
        ctx.textAlign = 'center';
        ctx.fillText('www.regard-de-nature.fr', width / 2, height - 50);
        
        ctx.shadowBlur = 0;

        // Convert to blob and upload
        console.log(`[renderBrandedImage] Converting canvas to blob...`);
        canvas.toBlob(async (blob) => {
          if (blob) {
            console.log(`[renderBrandedImage] Blob created: ${blob.size} bytes, type: ${blob.type}`);
            try {
              const url = await uploadToStorage(blob, draftId, format);
              console.log(`[renderBrandedImage] DONE - format: ${format}, url: ${url}`);
              resolve(url);
            } catch (uploadErr) {
              console.error(`[renderBrandedImage] Upload failed for ${format}:`, uploadErr);
              resolve(null);
            }
          } else {
            console.error(`[renderBrandedImage] canvas.toBlob returned null for ${format}`);
            resolve(null);
          }
        }, 'image/jpeg', 0.92);
        
      } catch (err) {
        console.error(`[renderBrandedImage] Render error for ${format}:`, err);
        resolve(null);
      }
    });
  }, []);

  /**
   * Génère les deux visuels (feed et story) et les upload
   * IMPORTANT: Cette fonction s'exécute en mode TEST ET PRODUCTION
   * Le mode dry_run ne bloque PAS le rendu, seulement la publication finale côté Make
   */
  const renderAndUploadMedia = useCallback(async (
    options: RenderOptions
  ): Promise<RenderedMedia> => {
    console.log('[useRenderedMediaUpload] Starting render with options:', JSON.stringify(options));
    setIsRendering(true);
    setError(null);
    
    const result: RenderedMedia = {
      feed_url: null,
      story_url: null,
    };

    try {
      // Generate feed (1080x1080)
      console.log('[useRenderedMediaUpload] Generating feed image (1080x1080)...');
      const feedUrl = await renderBrandedImage('feed', options);
      result.feed_url = feedUrl;
      console.log('[useRenderedMediaUpload] Feed URL generated:', feedUrl);

      // Generate story (1080x1920)
      console.log('[useRenderedMediaUpload] Generating story image (1080x1920)...');
      const storyUrl = await renderBrandedImage('story', options);
      result.story_url = storyUrl;
      console.log('[useRenderedMediaUpload] Story URL generated:', storyUrl);

      console.log('[useRenderedMediaUpload] FINAL RESULT:', JSON.stringify(result));
      
      if (!result.feed_url && !result.story_url) {
        console.warn('[useRenderedMediaUpload] WARNING: Both URLs are null! Check canvas/upload logs above.');
      }
      
      return result;
      
    } catch (err: any) {
      console.error('[useRenderedMediaUpload] RENDER ERROR:', err);
      setError(err.message);
      return result;
    } finally {
      setIsRendering(false);
    }
  }, [renderBrandedImage]);

  return {
    renderAndUploadMedia,
    isRendering,
    error,
  };
}
